import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { X, Plane, ArrowRight, Ellipsis, Trash2 } from '@/components/icons'
import { useForm } from 'react-hook-form'
import { Button } from './ui/button'
import { Avatar, AvatarFallback } from './ui/avatar'
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from './ui/dropdown-menu'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import { useRosterUpdate, useRosterDelete, useRosterGetById } from '../lib/hooks/roster'
import { DutyStatus } from '@@api/shared'
import { getInitials } from '../lib/utils'
import { DetailSkeleton } from './ui/skeleton-presets'
import { useTripLegsByCrewAndDateRange, useAircraftGetAll, useAircraftMaintenance } from '../lib/hooks'
import { PrntrCard } from './ui/prntr-input'
import { PickerRowField, SelectRowField, TextareaField } from './ui/prntr-fields'
import { AircraftPickerDrawer } from './AircraftPickerDrawer'
import { MaintenanceDayList } from './MaintenanceDayList'

interface FormData {
  dutyStatus: DutyStatus | null
  aircraftId: string
  note: string
}

const statusOptions: DutyStatus[] = [
  DutyStatus.OnDuty,
  DutyStatus.OffDuty,
  DutyStatus.Standby,
  DutyStatus.Training,
  DutyStatus.Leave,
  DutyStatus.Sick,
  DutyStatus.Positioning,
]

const dutyStatusLabels: Record<DutyStatus, string> = {
  [DutyStatus.OnDuty]: 'On Duty',
  [DutyStatus.OffDuty]: 'Off Duty',
  [DutyStatus.Standby]: 'Standby',
  [DutyStatus.Training]: 'Training',
  [DutyStatus.Leave]: 'Leave',
  [DutyStatus.Sick]: 'Sick',
  [DutyStatus.Positioning]: 'Positioning',
}

interface BaseProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

interface CreateProps extends BaseProps {
  crewId: string
  date: Date
  crew?: {
    id: string
    name: string
    firstName?: string | null
    lastName?: string | null
    avatarColor?: string | null
  }
  rosterId?: undefined
}

interface EditProps extends BaseProps {
  rosterId: string
  crewId?: undefined
  date?: undefined
}

export type SingleRosterEntryDrawerProps = CreateProps | EditProps

export function SingleRosterEntryDrawer(props: SingleRosterEntryDrawerProps) {
  const { open, onOpenChange, onSuccess } = props
  const navigate = useNavigate()
  const isCreating = !props.rosterId

  const [showAircraftPicker, setShowAircraftPicker] = useState(false)

  const updateMutation = useRosterUpdate()
  const deleteMutation = useRosterDelete()
  const { data: aircraftList = [] } = useAircraftGetAll()

  // Fetch the full roster entry when editing
  const { data: rosterEntry, isLoading: loadingEntry } = useRosterGetById(
    open && props.rosterId ? props.rosterId : null
  )

  // Derive values from fetched entry or create-mode props
  const crewId = isCreating ? props.crewId : rosterEntry?.crewId ?? ''
  const date = isCreating ? props.date : rosterEntry?.date ? new Date(rosterEntry.date) : new Date()
  const crew = rosterEntry?.crew ?? (isCreating && props.crew ? props.crew : null)
  const currentStatus = rosterEntry?.dutyStatus as DutyStatus ?? DutyStatus.OnDuty
  const currentNote = rosterEntry?.note?.body ?? ''
  const currentAircraftId = rosterEntry?.aircraftId ?? ''

  const {
    control,
    watch,
    setValue,
    reset,
    formState: { isDirty },
  } = useForm<FormData>({
    defaultValues: {
      dutyStatus: isCreating ? null : currentStatus,
      aircraftId: '',
      note: '',
    },
  })

  const watchedDutyStatus = watch('dutyStatus')
  const watchedAircraftId = watch('aircraftId')
  const watchedNote = watch('note')

  // Fetch flight info for this crew member on this date
  const utcDate = useMemo(() => new Date(dayjs(date).format('YYYY-MM-DD')), [date])
  const { data: tripLegs = [], isLoading: loadingTripLegs } = useTripLegsByCrewAndDateRange({
    crewIds: crewId ? [crewId] : [],
    startDate: utcDate,
    endDate: utcDate,
  })

  // Aircraft in maintenance on this date (all aircraft)
  const { getMaintenanceForDay } = useAircraftMaintenance()
  const maintenanceAircraft = getMaintenanceForDay(dayjs(date))

  const flightsForDate = useMemo(() => {
    const dateKey = dayjs(date).format('YYYY-MM-DD')
    return tripLegs.filter((leg: any) => {
      const legStart = dayjs(leg.departureDateTime).format('YYYY-MM-DD')
      const legEnd = dayjs(leg.arrivalDateTime).format('YYYY-MM-DD')
      return dateKey >= legStart && dateKey <= legEnd
    })
  }, [tripLegs, date])

  // Reset form when drawer opens or when entry finishes loading
  useEffect(() => {
    if (open) {
      reset({ dutyStatus: isCreating ? null : currentStatus, aircraftId: currentAircraftId, note: currentNote })
    }
  }, [open, currentStatus, currentAircraftId, currentNote, reset, isCreating])

  const handleClose = () => {
    reset({ dutyStatus: currentStatus, aircraftId: currentAircraftId, note: currentNote })
    onOpenChange(false)
  }

  const dateString = dayjs(date).format('YYYY-MM-DD')

  const handleSave = async () => {
    if (!watchedDutyStatus) return

    try {
      await updateMutation.mutateAsync({
        crewId,
        date: dateString,
        dutyStatus: watchedDutyStatus,
        aircraftId: watchedAircraftId,
        note: watchedNote || undefined,
        // Deliberate roster edit — the crew member should hear about it.
        // roster.update is default-deny, so this opt-in is required.
        notify: true,
      })
      const crewName = crew?.name ?? 'Crew'
      const dateStr = dayjs(date).format('MMM D')
      toast.success(`${crewName}'s status updated to ${dutyStatusLabels[watchedDutyStatus]} on ${dateStr}`)
      onSuccess?.()
      handleClose()
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update roster entry')
    }
  }

  const handleRemove = async () => {
    if (!props.rosterId) return

    try {
      await deleteMutation.mutateAsync({ id: props.rosterId })
      const crewName = crew?.name ?? 'Crew'
      const dateStr = dayjs(date).format('MMM D')
      toast.success(`${crewName}'s roster entry removed for ${dateStr}`)
      onSuccess?.()
      handleClose()
    } catch (error: any) {
      toast.error(error?.message || 'Failed to remove roster entry')
    }
  }

  const initials = crew ? getInitials(crew.firstName, crew.lastName) : ''
  const displayName = crew
    ? crew.firstName && crew.lastName
      ? `${crew.firstName} ${crew.lastName}`
      : crew.name
    : 'Unknown Crew'

  const isLoading = !isCreating && (loadingEntry || loadingTripLegs)

  return (
    <>
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="bg-surface-L0 max-h-[85dvh] flex flex-col">
          {/* Header with date */}
          <DrawerHeader className="flex items-center justify-between p-4">
            <DrawerTitle className="font-semibold text-lg text-content-primary-bold">
              {isLoading ? 'Loading...' : dayjs(date).format('MMMM D')}
            </DrawerTitle>
            <div className="flex items-center gap-3">
              {props.rosterId && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" size="icon" roundness="round" className="size-8">
                      <Ellipsis className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem variant="destructive" onClick={handleRemove}>
                      <Trash2 className="w-4 h-4" />
                      Remove
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <Button variant="secondary" size="icon" roundness="round" className="size-8" onClick={handleClose}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </DrawerHeader>

          {/* Content */}
          {isLoading ? (
            <DetailSkeleton rows={5} />
          ) : (
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain flex flex-col gap-4 px-4 pb-4">
            {/* Aircraft in maintenance for this date */}
            <MaintenanceDayList aircraft={maintenanceAircraft} />

            {/* Crew Member (read-only) */}
            <div className="flex flex-col gap-2">
              <label className="font-medium text-sm text-content-primary-bold">
                Crew Member
              </label>
              <div className="flex items-center gap-3 px-3 py-2 border border-input rounded-md bg-surface-L2">
                <Avatar className="size-6 shrink-0">
                  <AvatarFallback className="text-xs font-medium text-background" style={crew?.avatarColor ? { backgroundColor: crew.avatarColor } : undefined}>{initials}</AvatarFallback>
                </Avatar>
                <span className="text-sm text-content-primary-bold">{displayName}</span>
              </div>
            </div>

            <PrntrCard>
              <PickerRowField
                control={control}
                name="aircraftId"
                label="Aircraft"
                display={(value) => {
                  const selectedAircraft = aircraftList.find((a) => a.id === value)
                  return selectedAircraft
                    ? `${selectedAircraft.registration}${selectedAircraft.type ? ` · ${selectedAircraft.type}` : ''}`
                    : null
                }}
                onOpen={() => setShowAircraftPicker(true)}
              />
              <SelectRowField
                control={control}
                name="dutyStatus"
                label="Duty Status"
                options={statusOptions.map((status) => ({
                  value: status,
                  label: dutyStatusLabels[status],
                }))}
                placeholder="Select duty status"
              />
            </PrntrCard>

            <TextareaField
              control={control}
              name="note"
              label="Note (optional)"
              placeholder="Add a note for this period..."
            />

            {/* Flight info for this date */}
            {flightsForDate.length > 0 && (
              <div className="flex flex-col gap-2">
                {flightsForDate.map((flight: any) => (
                  <button
                    key={flight.id}
                    type="button"
                    onClick={() => {
                      onOpenChange(false)
                      navigate({
                        to: '/operations/trips/$tripId',
                        params: { tripId: flight.trip.id },
                        search: { legId: flight.id } as any,
                      })
                    }}
                    className="flex items-center gap-2 bg-surface-L2 rounded-lg p-2 w-full text-left"
                  >
                    <Plane className="size-3 text-content-primary-bold shrink-0" />
                    <span className="text-sm text-content-primary-bold">
                      {flight.departureAirportIcao}
                    </span>
                    <ArrowRight className="size-4 text-content-primary-bold shrink-0" />
                    <span className="text-sm text-content-primary-bold">
                      {flight.arrivalAirportIcao}
                    </span>
                    {flight.aircraft?.registration && (
                      <>
                        <span className="text-sm text-content-primary-bold">•</span>
                        <span className="text-sm text-content-primary-bold">
                          {flight.aircraft.registration}
                        </span>
                      </>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          )}

          {/* Footer with buttons */}
          <DrawerFooter className="flex-row">
            <Button variant="secondary" size="mini" onClick={handleClose} className="flex-1">
              Cancel
            </Button>
            <Button
              variant="primary"
              size="mini"
              onClick={handleSave}
              disabled={updateMutation.isPending || !isDirty || !watchedDutyStatus || !watchedAircraftId}
              className="flex-1"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <AircraftPickerDrawer
        isOpen={showAircraftPicker}
        onClose={() => setShowAircraftPicker(false)}
        onSelect={(aircraftId) => {
          setValue('aircraftId', aircraftId, { shouldDirty: true })
          setShowAircraftPicker(false)
        }}
        selectedAircraftId={watchedAircraftId || undefined}
      />
    </>
  )
}
